/**
 * Mobile sticky ATC — top variant bar.
 * Imports the bottom bar class from a sibling file.
 * Both custom elements are registered here for a single compiled asset.
 *
 * <sticky-mobile-top>   — floating top variant selector (slides from above)
 * <sticky-mobile-bottom>— compact ATC bar, lives in sticky-atc-mobile-bottom.js
 *
 * Scroll trigger: IntersectionObserver on form.product-single__form.
 * Variant sync: clicking real PDP inputs fires all existing event chains
 *   (image carousel, price updater, etc.) without duplicating logic.
 */
import './sticky-atc-mobile-bottom';

class StickyMobileTop extends HTMLElement {
  connectedCallback() {
    this.bottomEl = document.querySelector('sticky-mobile-bottom');

    // Configurable selectors — override via data attributes in the Liquid snippet
    // to reuse this component in sections with different form/input markup.
    this.formSel = this.dataset.formSelector || 'form.product-single__form';
    this.variantInputSel = this.dataset.variantInputSelector || 'input[data-variant-input]';

    // Build variant lookup from hidden data div
    this.variantMap = {};
    const dataEl = document.getElementById('sticky-mobile-variants-data');

    if (dataEl) {
      dataEl.querySelectorAll('.sticky-mobile-variant-item').forEach((item) => {
        this.variantMap[item.dataset.key] = {
          id: item.dataset.id,
          available: item.dataset.available === 'true',
          price: item.dataset.price,
        };
      });
    }

    this.swatches = this.querySelectorAll('.sticky-top__swatch');
    this.sizeSelects = this.querySelectorAll('.sticky-top__size-select');

    // Guard flag to prevent sync loops when we programmatically click PDP inputs
    this._syncing = false;

    this.swatches.forEach((btn) => {
      btn.addEventListener('click', this.onSwatchClick.bind(this));
    });

    this.sizeSelects.forEach((sel) => {
      sel.addEventListener('change', this.onSizeChange.bind(this));
    });

    // Reverse sync: when main form variant changes, update top bar to match
    document.querySelectorAll(this.variantInputSel).forEach((input) => {
      input.addEventListener('change', this.onMainFormChange.bind(this));
    });

    this.setupObserver();
    this.setHeaderOffset();

    // Remove the inline display:none set in Liquid (prevents CSS transition flash on load).
    // CSS transitions don't fire when coming out of display:none, so the elements
    // appear silently at their off-screen transform position.
    this.style.display = '';
    if (this.bottomEl) this.bottomEl.style.display = '';

    // Enable transitions on the next paint so no animation fires during init.
    requestAnimationFrame(() => {
      this.classList.add('is-animated');
      if (this.bottomEl) this.bottomEl.classList.add('is-animated');
    });
  }

  setHeaderOffset() {
    const headerEl = document.querySelector('[data-section-type="header"]');
    const headerH = headerEl ? headerEl.offsetHeight : 0;

    if (headerH > 0) {
      this.style.top = `${headerH}px`;
    }
  }

  setupObserver() {
    // Observe a scroll target to decide when to show the sticky bars.
    // If data-scroll-target is set, use that selector (scoped to the section).
    // Otherwise fall back to the full .shopify-section wrapper.
    const sectionEl = this.closest('.shopify-section');
    let section = sectionEl;

    if (this.dataset.scrollTarget && sectionEl) {
      const target = sectionEl.querySelector(this.dataset.scrollTarget);

      if (target) {
        section = target;
      }
    }

    if (!section) return;

    const updateVisibility = () => {
      // Hide bars while the cart drawer is open.
      const cartOpen = document.documentElement.classList.contains('cart-open');
      const scrolledPast = section.getBoundingClientRect().bottom < 0;
      const show = scrolledPast && !cartOpen;

      if (this.dataset.singleVariant !== 'true') {
        this.dataset.visible = String(show);
      }

      if (this.bottomEl) {
        this.bottomEl.dataset.visible = String(show);
      }
    };

    // Scroll trigger (RAF-throttled).
    let ticking = false;

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;

        ticking = true;

        requestAnimationFrame(() => {
          updateVisibility();
          ticking = false;
        });
      },
      { passive: true }
    );

    // Watch for cart-open class being added/removed on <html>.
    new MutationObserver(updateVisibility).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  onSwatchClick(event) {
    if (this._syncing) return;

    const btn = event.currentTarget;
    const { colorName, optionName } = btn.dataset;

    // Update active swatch UI
    const group = btn.closest('.sticky-top__swatches');

    if (group) {
      group.querySelectorAll('.sticky-top__swatch').forEach((s) => {
        s.dataset.selected = 'false';
        s.setAttribute('aria-pressed', 'false');
      });
      btn.dataset.selected = 'true';
      btn.setAttribute('aria-pressed', 'true');
    }

    // Click the real PDP input — triggers image carousel and all other listeners
    this._syncing = true;
    const pdpInput = document.querySelector(
      `${this.variantInputSel}[name="${CSS.escape(optionName)}"][value="${CSS.escape(colorName)}"]`
    );

    if (pdpInput) pdpInput.click();
    this._syncing = false;

    this.refreshBottomBar();
  }

  onSizeChange(event) {
    if (this._syncing) return;

    const sel = event.currentTarget;
    const { optionName } = sel.dataset;
    const { value } = sel;

    this._syncing = true;
    const pdpInput = document.querySelector(
      `${this.variantInputSel}[name="${CSS.escape(optionName)}"][value="${CSS.escape(value)}"]`
    );

    if (pdpInput) pdpInput.click();
    this._syncing = false;

    this.refreshBottomBar();
  }

  onMainFormChange(event) {
    if (this._syncing) return;

    const { name, value } = event.target;

    this._syncing = true;

    this.swatches.forEach((btn) => {
      if (btn.dataset.optionName === name) {
        const isSelected = btn.dataset.colorName === value;

        btn.dataset.selected = String(isSelected);
        btn.setAttribute('aria-pressed', String(isSelected));
      }
    });

    this.sizeSelects.forEach((sel) => {
      if (sel.dataset.optionName === name) sel.value = value;
    });

    this._syncing = false;
    this.refreshBottomBar();
  }

  refreshBottomBar() {
    if (!this.bottomEl) return;

    // Prefer reading the main form's selected variant option (most accurate)
    const mainSelect = document.querySelector(`${this.formSel} select[name="id"]`);

    if (mainSelect) {
      const selectedOpt = mainSelect.querySelector(`option[value="${mainSelect.value}"]`);

      if (selectedOpt) {
        const price = selectedOpt.dataset.price;
        const available = selectedOpt.dataset.available !== 'false';

        this.bottomEl.updateVariant({ price, available });
        return;
      }
    }

    // Fallback: derive from top bar state + variantMap
    const selectedValues = [];

    this.querySelectorAll('.sticky-top__swatches').forEach((group) => {
      const sel = group.querySelector('.sticky-top__swatch[data-selected="true"]');

      if (sel) selectedValues.push(sel.dataset.colorName);
    });

    this.sizeSelects.forEach((sel) => selectedValues.push(sel.value));

    const variant = this.variantMap[selectedValues.join('-')];

    if (variant) {
      this.bottomEl.updateVariant({ price: variant.price, available: variant.available });
    }
  }
}

if (!customElements.get('sticky-mobile-top')) {
  customElements.define('sticky-mobile-top', StickyMobileTop);
}
